import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  Autocomplete,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowLeft,
  Save,
  Download,
  Send,
  Plus,
  Trash2,
  Info,
  Eye,
  X,
} from "lucide-react";
import {
  fetchQuote,
  createQuote,
  updateQuote,
  downloadQuotePdf,
  sendQuoteEmail,
} from "../../../../../redux/actions/facturationAction";
import { fetchPricing } from "../../../../../redux/actions/pricingActions";
import { backendClient } from "../../../../../api/backend";
import { enqueueSnackbar } from "notistack";

const LANGUAGE_OPTIONS = [
  { value: "fr", label: "Français" },
  { value: "en", label: "English" },
];

const PAYMENT_OPTIONS = [
  "Virement bancaire",
  "Carte bancaire",
  "Chèque",
  "Espèces",
];

// ── Pricing helpers ─────────────────────────────────────────────────────────

/**
 * Resolve the unit price (monthly per seat/user) for a plan from the API pricing data.
 * For Pro with volume tiers, picks the tier matching numUsers.
 * Returns { price, tierLabel, annualTotal } or null.
 */
function resolvePlanPrice(pricingPlans, planSlug, numUsers) {
  if (!pricingPlans) return null;
  const plan = pricingPlans.find((p) => p.slug === planSlug);
  if (!plan) return null;

  // Enterprise — custom pricing
  if (plan.is_custom_pricing) return null;

  // Per-seat with volume tiers (Pro)
  if (plan.billing_mode === "per_seat" && plan.volume_tiers) {
    // volume_tiers is { "ma": [...] } or just an array
    const tiers = Array.isArray(plan.volume_tiers)
      ? plan.volume_tiers
      : Object.values(plan.volume_tiers)[0] || [];

    const tier = tiers.find((t) => numUsers >= t.min && numUsers <= t.max);
    if (tier) {
      return {
        price: tier.monthly_per_seat,
        annualPerSeat: tier.annual_per_seat,
        tierLabel: `${tier.min}-${tier.max} utilisateurs`,
      };
    }
    // Fallback to last tier if numUsers exceeds max
    if (tiers.length > 0) {
      const last = tiers[tiers.length - 1];
      return {
        price: last.monthly_per_seat,
        annualPerSeat: last.annual_per_seat,
        tierLabel: `${last.min}+ utilisateurs`,
      };
    }
  }

  // Flat pricing (Starter)
  if (plan.annual_price) {
    return {
      price: plan.annual_price,
      annualPerSeat: plan.annual_total,
      tierLabel: null,
    };
  }
  if (plan.monthly_price) {
    return {
      price: plan.monthly_price,
      annualPerSeat: null,
      tierLabel: null,
    };
  }

  return null;
}

function getPlanConstraints(pricingPlans, planSlug) {
  if (!pricingPlans) return { annualOnly: false, maxSeats: 999, minSeats: 1 };
  const plan = pricingPlans.find((p) => p.slug === planSlug);
  if (!plan) return { annualOnly: false, maxSeats: 999, minSeats: 1 };
  return {
    annualOnly: plan.annual_only || false,
    maxSeats: plan.max_seats || 999,
    minSeats: plan.min_seats || 1,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

const QuoteForm = ({ quoteId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { currentQuote } = useSelector((state) => state.facturation);
  const pricingData = useSelector((state) => state.pricing.data);
  const isEdit = Boolean(quoteId);

  const [form, setForm] = useState({
    language: "fr",
    marketing_organization_id: null,
    client_name: "",
    client_ice: "",
    client_address: "",
    client_city: "",
    client_country: "",
    plan: "pro",
    num_users: 1,
    billing_cycle: "annual",
    unit_price_ht: "990",
    currency: "MAD",
    discount_percent: "0",
    tva_rate: "20",
    payment_method: "Virement bancaire",
    validity_days: 30,
    include_signature: true,
    custom_lines: [],
    notes: "",
  });

  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const orgSearchTimer = useRef(null);

  // Derived pricing state
  const pricingPlans = pricingData?.plans || null;
  const pricingCurrency = pricingData?.currency || "MAD";
  const isEnterprise = form.plan === "enterprise";
  const constraints = useMemo(
    () => getPlanConstraints(pricingPlans, form.plan),
    [pricingPlans, form.plan]
  );
  const resolvedPrice = useMemo(
    () => resolvePlanPrice(pricingPlans, form.plan, parseInt(form.num_users) || 1),
    [pricingPlans, form.plan, form.num_users]
  );

  // Load data
  useEffect(() => {
    if (!pricingData || pricingData.region !== "ma") {
      dispatch(fetchPricing("ma"));
    }
    if (isEdit) {
      dispatch(fetchQuote(quoteId));
    }
  }, [dispatch, quoteId, isEdit]);

  // Pre-select org from URL query param (?org=<orgId>)
  const preselectedOrgId = searchParams.get("org");
  const preselectedOrgLoaded = useRef(false);
  useEffect(() => {
    if (!preselectedOrgId || isEdit || preselectedOrgLoaded.current) return;
    preselectedOrgLoaded.current = true;
    backendClient.getMarketingOrganization(preselectedOrgId)
      .then((org) => {
        if (!org) return;
        setSelectedOrg(org);
        setForm((prev) => ({
          ...prev,
          marketing_organization_id: org.id,
          client_name: org.name || "",
          client_ice: org.ice || "",
          client_address: org.address || "",
          client_city: org.city || "",
          client_country: org.country || "",
        }));
      })
      .catch(() => {});
  }, [preselectedOrgId, isEdit]);

  // Debounced org search
  const handleOrgSearch = useCallback((inputValue) => {
    if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
    if (!inputValue || inputValue.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    setOrgSearchLoading(true);
    orgSearchTimer.current = setTimeout(async () => {
      try {
        const data = await backendClient.getMarketingOrganizations({ search: inputValue });
        setOrgSearchResults(data.items || data);
      } catch {
        setOrgSearchResults([]);
      } finally {
        setOrgSearchLoading(false);
      }
    }, 300);
  }, []);

  // Fill form when editing
  useEffect(() => {
    if (isEdit && currentQuote && currentQuote.id === quoteId) {
      setForm({
        language: currentQuote.language || "fr",
        marketing_organization_id: currentQuote.marketing_organization_id,
        client_name: currentQuote.client_name || "",
        client_ice: currentQuote.client_ice || "",
        client_address: currentQuote.client_address || "",
        client_city: currentQuote.client_city || "",
        client_country: currentQuote.client_country || "",
        plan: currentQuote.plan || "pro",
        num_users: currentQuote.num_users || 1,
        billing_cycle: currentQuote.billing_cycle || "annual",
        unit_price_ht: String(currentQuote.unit_price_ht || ""),
        currency: currentQuote.currency || "MAD",
        discount_percent: String(currentQuote.discount_percent || "0"),
        tva_rate: String(currentQuote.tva_rate || "20"),
        payment_method: currentQuote.payment_method || "Virement bancaire",
        validity_days: currentQuote.validity_days || 30,
        include_signature: currentQuote.include_signature !== false,
        custom_lines: currentQuote.custom_lines || [],
        notes: currentQuote.notes || "",
      });

      if (currentQuote.marketing_organization_id) {
        backendClient.getMarketingOrganization(currentQuote.marketing_organization_id)
          .then((org) => { if (org) setSelectedOrg(org); })
          .catch(() => {});
      }
    }
  }, [isEdit, currentQuote, quoteId]);

  // Auto-fill price from pricing API when plan/users change (not when editing)
  useEffect(() => {
    if (isEnterprise) return; // enterprise = manual
    if (resolvedPrice) {
      setForm((prev) => ({
        ...prev,
        unit_price_ht: String(resolvedPrice.price),
        currency: pricingCurrency,
      }));
    }
  }, [resolvedPrice, pricingCurrency, isEnterprise]);

  // Enforce constraints when plan changes
  useEffect(() => {
    setForm((prev) => {
      const updates = {};
      if (constraints.annualOnly && prev.billing_cycle !== "annual") {
        updates.billing_cycle = "annual";
      }
      if (parseInt(prev.num_users) > constraints.maxSeats) {
        updates.num_users = constraints.maxSeats;
      }
      if (parseInt(prev.num_users) < constraints.minSeats) {
        updates.num_users = constraints.minSeats;
      }
      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [constraints]);

  // Build plan options from pricing API
  const planOptions = useMemo(() => {
    if (!pricingPlans) {
      return [
        { value: "starter", label: "Essentiel" },
        { value: "pro", label: "Pro" },
        { value: "enterprise", label: "Entreprise" },
      ];
    }
    return pricingPlans.map((p) => ({
      value: p.slug,
      label: p.display_name,
    }));
  }, [pricingPlans]);

  // Computed totals — for Pro, unit_price_ht is monthly/user. Annual total = price * 12 * users
  const computed = useMemo(() => {
    const unitPrice = parseFloat(form.unit_price_ht) || 0;
    const numUsers = parseInt(form.num_users) || 1;
    const discountPct = parseFloat(form.discount_percent) || 0;
    const tvaRate = parseFloat(form.tva_rate) || 20;

    // For annual billing with per-seat pricing, use the annual_per_seat if available
    let subtotalPlan;
    if (resolvedPrice?.annualPerSeat && form.billing_cycle === "annual") {
      subtotalPlan = resolvedPrice.annualPerSeat * numUsers;
    } else {
      // Monthly or flat: price * users (for monthly cycle, or annual flat)
      subtotalPlan = unitPrice * numUsers;
      if (form.billing_cycle === "annual" && !resolvedPrice?.annualPerSeat) {
        subtotalPlan = unitPrice * 12 * numUsers;
      }
    }

    const discountAmount = (subtotalPlan * discountPct) / 100;
    let totalHt = subtotalPlan - discountAmount;

    // Add custom lines
    (form.custom_lines || []).forEach((line) => {
      totalHt += parseFloat(line.total) || 0;
    });

    const tvaAmount = (totalHt * tvaRate) / 100;
    const totalTtc = totalHt + tvaAmount;

    return {
      subtotalPlan: subtotalPlan.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      totalHt: totalHt.toFixed(2),
      tvaAmount: tvaAmount.toFixed(2),
      totalTtc: totalTtc.toFixed(2),
      unitPriceLabel: `${unitPrice} ${form.currency}/utilisateur/mois`,
    };
  }, [form.unit_price_ht, form.num_users, form.discount_percent, form.tva_rate, form.custom_lines, form.billing_cycle, form.currency, resolvedPrice]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleOrgSelect = (_, org) => {
    setSelectedOrg(org);
    if (org) {
      setForm((prev) => ({
        ...prev,
        marketing_organization_id: org.id,
        client_name: org.name,
        client_ice: org.ice || "",
        client_address: org.address || "",
        client_city: org.city || "",
        client_country: org.country || "",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        marketing_organization_id: null,
        client_name: "",
        client_ice: "",
        client_address: "",
        client_city: "",
        client_country: "",
      }));
    }
  };

  // Custom lines management
  const addCustomLine = () => {
    setForm((prev) => ({
      ...prev,
      custom_lines: [...(prev.custom_lines || []), { description: "", qty: 1, unit_price: 0, total: 0 }],
    }));
  };

  const updateCustomLine = (index, field, value) => {
    setForm((prev) => {
      const lines = [...(prev.custom_lines || [])];
      lines[index] = { ...lines[index], [field]: value };
      if (field === "qty" || field === "unit_price") {
        lines[index].total = (parseFloat(lines[index].qty) || 0) * (parseFloat(lines[index].unit_price) || 0);
      }
      return { ...prev, custom_lines: lines };
    });
  };

  const removeCustomLine = (index) => {
    setForm((prev) => ({
      ...prev,
      custom_lines: prev.custom_lines.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.client_name.trim()) {
      enqueueSnackbar("Nom du client requis", { variant: "warning" });
      return;
    }
    if (!form.unit_price_ht || parseFloat(form.unit_price_ht) <= 0) {
      enqueueSnackbar("Prix unitaire HT requis", { variant: "warning" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_price_ht: parseFloat(form.unit_price_ht),
        discount_percent: parseFloat(form.discount_percent) || 0,
        tva_rate: parseFloat(form.tva_rate) || 20,
        num_users: parseInt(form.num_users) || 1,
        validity_days: parseInt(form.validity_days) || 30,
        custom_lines: (form.custom_lines || []).filter((l) => l.description.trim()),
      };

      if (isEdit) {
        await dispatch(updateQuote(quoteId, payload));
        enqueueSnackbar("Devis mis à jour", { variant: "success" });
      } else {
        const result = await dispatch(createQuote(payload));
        enqueueSnackbar("Devis créé", { variant: "success" });
        navigate(`/admin/facturation/devis/${result.id}`);
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!isEdit) return;
    setPdfLoading(true);
    try {
      await dispatch(downloadQuotePdf(quoteId, { clientName: form.client_name, lang: form.language }));
      enqueueSnackbar("PDF téléchargé", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Erreur PDF", { variant: "error" });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!isEdit) return;
    setPreviewLoading(true);
    try {
      const blob = await backendClient.downloadQuotePdf(quoteId);
      const prefix = form.language === "en" ? "Quote" : "Devis";
      const name = form.client_name ? `${prefix} ${form.client_name}.pdf` : `${prefix}.pdf`;
      const file = new File([blob], name, { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      enqueueSnackbar("Erreur prévisualisation", { variant: "error" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleSendEmail = async () => {
    if (!isEdit) return;
    try {
      await dispatch(sendQuoteEmail(quoteId));
      enqueueSnackbar("Devis envoyé par email", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Erreur d'envoi", { variant: "error" });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton size="small" onClick={() => navigate("/admin/facturation/devis")}>
            <ArrowLeft size={18} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEdit ? `Devis ${currentQuote?.quote_number || ""}` : "Nouveau devis"}
          </Typography>
          {isEdit && currentQuote && (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                fontWeight: 600,
              }}
            >
              {currentQuote.status?.toUpperCase()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isEdit && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={previewLoading ? <CircularProgress size={14} /> : <Eye size={14} />}
                onClick={handlePreview}
                disabled={previewLoading}
                sx={{ textTransform: "none" }}
              >
                Aperçu
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={14} /> : <Download size={14} />}
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                sx={{ textTransform: "none" }}
              >
                PDF
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Send size={14} />}
                onClick={handleSendEmail}
                disabled={!isEdit}
                sx={{ textTransform: "none" }}
              >
                Envoyer
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isEdit ? "Mettre à jour" : "Créer"}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left column: Client + Plan */}
        <Grid item xs={12} md={8}>
          {/* Client Section */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Client
            </Typography>

            <Autocomplete
              options={orgSearchResults}
              getOptionLabel={(o) => o.name || ""}
              value={selectedOrg}
              onChange={handleOrgSelect}
              onInputChange={(_, value, reason) => {
                if (reason === "input") handleOrgSearch(value);
                if (reason === "clear") setOrgSearchResults([]);
              }}
              loading={orgSearchLoading}
              filterOptions={(x) => x}
              noOptionsText="Tapez pour rechercher..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organisation (existante)"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {orgSearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              size="small"
            />
          </Paper>

          {/* Plan & Pricing Section */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Offre & Tarification
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan</InputLabel>
                  <Select value={form.plan} onChange={handleChange("plan")} label="Plan">
                    {planOptions.map((p) => (
                      <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth size="small" label="Nb utilisateurs" type="number"
                  value={form.num_users} onChange={handleChange("num_users")}
                  inputProps={{ min: constraints.minSeats, max: constraints.maxSeats }}
                  disabled={constraints.maxSeats === 1}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cycle</InputLabel>
                  <Select
                    value={form.billing_cycle}
                    onChange={handleChange("billing_cycle")}
                    label="Cycle"
                    disabled={constraints.annualOnly}
                  >
                    <MenuItem value="annual">Annuel</MenuItem>
                    <MenuItem value="monthly" disabled={constraints.annualOnly}>Mensuel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth size="small" label="Devise" value={form.currency} disabled
                  helperText="Depuis tarifs"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth size="small"
                  label={`Prix/utilisateur/mois (${form.currency})`}
                  type="number"
                  value={form.unit_price_ht}
                  onChange={handleChange("unit_price_ht")}
                  inputProps={{ min: 0, step: "0.01" }}
                  disabled={!isEnterprise}
                />
              </Grid>

              {/* Tier indicator for Pro */}
              {resolvedPrice?.tierLabel && !isEnterprise && (
                <Grid item xs={12}>
                  <Chip
                    icon={<Info size={14} />}
                    label={`Palier ${resolvedPrice.tierLabel} — ${resolvedPrice.price} ${form.currency}/util./mois${resolvedPrice.annualPerSeat ? ` (${resolvedPrice.annualPerSeat.toLocaleString("fr-FR")} ${form.currency}/util./an)` : ""}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth size="small" label="Remise (%)" type="number"
                  value={form.discount_percent} onChange={handleChange("discount_percent")}
                  inputProps={{ min: 0, max: 100, step: "0.01" }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth size="small" label="TVA (%)" type="number"
                  value={form.tva_rate} onChange={handleChange("tva_rate")}
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
            </Grid>

            {/* Custom lines */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                  Lignes supplémentaires (optionnel)
                </Typography>
                <Button size="small" startIcon={<Plus size={12} />} onClick={addCustomLine} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                  Ajouter
                </Button>
              </Box>
              {(form.custom_lines || []).map((line, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                  <Grid item xs={5}>
                    <TextField fullWidth size="small" placeholder="Description" value={line.description} onChange={(e) => updateCustomLine(idx, "description", e.target.value)} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" placeholder="Qté" type="number" value={line.qty} onChange={(e) => updateCustomLine(idx, "qty", parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" placeholder="Prix unit." type="number" value={line.unit_price} onChange={(e) => updateCustomLine(idx, "unit_price", parseFloat(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" placeholder="Total" type="number" value={line.total} disabled />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" onClick={() => removeCustomLine(idx)} sx={{ mt: 0.5 }}>
                      <Trash2 size={14} />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Paper>

          {/* Notes */}
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Notes internes
            </Typography>
            <TextField
              fullWidth multiline rows={3} size="small" placeholder="Notes visibles uniquement par l'admin..."
              value={form.notes} onChange={handleChange("notes")}
            />
          </Paper>
        </Grid>

        {/* Right column: Options + Summary */}
        <Grid item xs={12} md={4}>
          {/* Options */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Options
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Langue du devis</InputLabel>
              <Select value={form.language} onChange={handleChange("language")} label="Langue du devis">
                {LANGUAGE_OPTIONS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Modalité de paiement</InputLabel>
              <Select value={form.payment_method} onChange={handleChange("payment_method")} label="Modalité de paiement">
                {PAYMENT_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth size="small" label="Validité (jours)" type="number"
              value={form.validity_days} onChange={handleChange("validity_days")}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.include_signature}
                  onChange={(e) => setForm((prev) => ({ ...prev, include_signature: e.target.checked }))}
                  size="small"
                />
              }
              label={<Typography variant="body2">Inclure la signature</Typography>}
            />
          </Paper>

          {/* Pricing Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem", color: theme.palette.text.secondary }}>
              Récapitulatif
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  {form.num_users > 1
                    ? `${form.num_users} util. × ${form.unit_price_ht} ${form.currency}/mois × ${form.billing_cycle === "annual" ? "12 mois" : "1 mois"}`
                    : `Abonnement ${form.billing_cycle === "annual" ? "annuel" : "mensuel"}`}
                </Typography>
                <Typography variant="body2">{computed.subtotalPlan} {form.currency}</Typography>
              </Box>

              {parseFloat(form.discount_percent) > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>
                    Remise ({form.discount_percent}%)
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>
                    -{computed.discountAmount} {form.currency}
                  </Typography>
                </Box>
              )}

              {(form.custom_lines || []).filter((l) => l.description).map((line, idx) => (
                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "60%" }}>
                    {line.description}
                  </Typography>
                  <Typography variant="body2">{(parseFloat(line.total) || 0).toFixed(2)} {form.currency}</Typography>
                </Box>
              ))}

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Total HT</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{computed.totalHt} {form.currency}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">TVA ({form.tva_rate}%)</Typography>
                <Typography variant="body2">{computed.tvaAmount} {form.currency}</Typography>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>Total TTC</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: "1.1rem", color: theme.palette.primary.main }}>
                  {computed.totalTtc} {form.currency}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* PDF Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Aperçu — {currentQuote?.quote_number || "Devis"}
          </Typography>
          <IconButton size="small" onClick={handleClosePreview}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Aperçu du devis"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default QuoteForm;
